import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom';
import { HiOutlinePencil, HiOutlinePhone, HiOutlineMail, HiOutlineLocationMarker, HiOutlineMap, HiOutlineHashtag, HiOutlineInformationCircle, HiOutlineEmojiSad } from 'react-icons/hi';
import AuthenticationService from '../service/AuthenticationService'
import ProfileService from '../service/ProfileService';
import '../css/admin.css'
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Toast from '../components/Toast';

// The API sometimes returns the string 'null' for empty fields.
const clean = (value) => (value == null || value === 'null' ? '' : String(value))

const validate = ({ userName, mobileNumber, address, state, pincode }) => {
    const errors = {}
    if (userName.trim().length < 3 || userName.trim().length > 20) {
        errors.userName = 'Name must be between 3 and 20 characters.'
    }
    if (!mobileNumber.trim()) {
        errors.mobileNumber = 'Please enter your mobile number.'
    } else if (mobileNumber.trim().length > 15) {
        errors.mobileNumber = 'Mobile number looks too long.'
    }
    if (address.trim().length < 3) {
        errors.address = 'Please enter your delivery address.'
    }
    if (!state.trim()) {
        errors.state = 'Please enter your state.'
    }
    if (!/^\d{6}$/.test(pincode.trim())) {
        errors.pincode = 'Please enter a valid 6-digit pincode.'
    }
    return errors
}

const UpdateSettings = () => {

    const location = useLocation();
    // id: page to return to after saving; editAddress: opened from checkout's "Edit" address.
    const { id, editAddress } = location.state || {};
    const history = useHistory();

    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState('')
    const [toast, setToast] = useState('')
    const [userName, setUserName] = useState('')
    const [email, setEmail] = useState('')
    const [mobileNumber, setMobileNumber] = useState('')
    const [address, setAddress] = useState('')
    const [state, setState] = useState('')
    const [pincode, setPincode] = useState('')
    const [redirectPage] = useState(id)
    const [userEdit, setUserEdit] = useState(false)
    const [errors, setErrors] = useState({})
    const [saved, setSaved] = useState(null)

    const loadProfile = () => {
        setIsLoading(true)
        setLoadError(false)
        AuthenticationService.getUserDetails().then(userres => {
            if (userres.status) {
                const data = userres.data.data
                const profile = {
                    userName: clean(data.userName),
                    mobileNumber: clean(data.mobileNumber),
                    address: clean(data.address1),
                    state: clean(data.state),
                    pincode: clean(data.location)
                }
                setUserName(profile.userName)
                setEmail(clean(data.email))
                setMobileNumber(profile.mobileNumber)
                setAddress(profile.address)
                setState(profile.state)
                setPincode(profile.pincode)
                setSaved(profile)
                // Open the form straight away when required details are missing or the address is being edited.
                if (editAddress || Object.keys(validate(profile)).length > 0) {
                    setUserEdit(true)
                }
                setIsLoading(false)
            }
        }).catch(error => {
            console.log(error)
            if (error?.response?.status === 401) {
                history.push("/login")
                return
            }
            setLoadError(true)
            setIsLoading(false)
        });
    }

    useEffect(() => {
        loadProfile()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onClickSubmit = (event) => {
        event.preventDefault();
        const values = { userName, mobileNumber, address, state, pincode }
        const found = validate(values)
        setErrors(found)
        setSaveError('')
        if (Object.keys(found).length > 0) return

        setIsSaving(true)
        const formData = new FormData();
        formData.append("location", pincode.trim());
        formData.append("homeName", userName.trim());
        formData.append("address1", address.trim());
        formData.append("state", state.trim());
        formData.append("mobileNumber", mobileNumber.trim());

        ProfileService.updateUserPreferences(formData).then(res => {
            setIsSaving(false)
            if (res.status === 200) {
                if (redirectPage) {
                    history.push({
                        pathname: redirectPage
                    });
                    return
                }
                setSaved(values)
                setUserEdit(false)
                setToast('Profile updated')
            }
        }).catch(error => {
            setIsSaving(false)
            setSaveError('Could not save your profile. Please try again.')
        });
    }

    const cancelEdit = () => {
        if (redirectPage) {
            history.push({ pathname: redirectPage })
            return
        }
        if (saved) {
            setUserName(saved.userName)
            setMobileNumber(saved.mobileNumber)
            setAddress(saved.address)
            setState(saved.state)
            setPincode(saved.pincode)
        }
        setErrors({})
        setSaveError('')
        setUserEdit(false)
    }

    const field = (key, label, input, hint) => (
        <div className={key === 'address' ? 'mv-field mv-field-full' : 'mv-field'}>
            <label className='mv-label' htmlFor={`profile-${key}`}>{label}</label>
            {input}
            {errors[key] ? <span className='mv-field-error' id={`profile-${key}-error`}>{errors[key]}</span>
                : hint && <span className='mv-field-hint'>{hint}</span>}
        </div>
    )

    const inputProps = (key) => ({
        id: `profile-${key}`,
        'aria-invalid': errors[key] ? 'true' : undefined,
        'aria-describedby': errors[key] ? `profile-${key}-error` : undefined
    })

    if (isLoading) {
        return (
            <main className='mv-page mv-settings' aria-busy='true'>
                <Skeleton width='180px' height={30} />
                <Skeleton height={320} radius='14px' style={{ marginTop: 20 }} />
            </main>
        )
    }

    if (loadError) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load your profile'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={loadProfile} />
            </main>
        )
    }

    const canCancel = saved && Object.keys(validate(saved)).length === 0

    return (
        <main className='mv-page mv-settings'>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>My profile</h1>
                    <p className='mv-page-sub'>Your contact and delivery details.</p>
                </div>
            </div>

            {redirectPage &&
                <div className='mv-alert mv-alert-info mv-settings-banner' role='status'>
                    <HiOutlineInformationCircle /> {editAddress
                        ? 'Update your delivery details. We will take you back to review your order after saving.'
                        : 'Please complete your profile to continue. We use these details to deliver your order.'}
                </div>}

            {!userEdit &&
                <section className='mv-card mv-profile'>
                    <div className='mv-profile-head'>
                        <span className='mv-profile-avatar' aria-hidden='true'>{(userName || '?').charAt(0).toUpperCase()}</span>
                        <div className='mv-profile-id'>
                            <span className='mv-profile-name'>{userName}</span>
                            {email && <span className='mv-page-sub'>{email}</span>}
                        </div>
                        <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' onClick={() => setUserEdit(true)}>
                            <HiOutlinePencil /> Edit
                        </button>
                    </div>

                    <dl className='mv-profile-list'>
                        <div className='mv-profile-row'>
                            <dt><HiOutlinePhone /> Mobile</dt>
                            <dd>{mobileNumber || '–'}</dd>
                        </div>
                        {email &&
                            <div className='mv-profile-row'>
                                <dt><HiOutlineMail /> Email</dt>
                                <dd>{email}</dd>
                            </div>}
                        <div className='mv-profile-row'>
                            <dt><HiOutlineLocationMarker /> Address</dt>
                            <dd className='mv-profile-address'>{address || '–'}</dd>
                        </div>
                        <div className='mv-profile-row'>
                            <dt><HiOutlineMap /> State</dt>
                            <dd>{state || '–'}</dd>
                        </div>
                        <div className='mv-profile-row'>
                            <dt><HiOutlineHashtag /> Pincode</dt>
                            <dd>{pincode || '–'}</dd>
                        </div>
                    </dl>
                </section>
            }

            {userEdit &&
                <form className='mv-card mv-profile-form' onSubmit={onClickSubmit} noValidate>
                    <h2 className='mv-card-title'>Edit profile</h2>
                    <div className='mv-form-grid'>
                        {field('userName', 'Name',
                            <input {...inputProps('userName')} className='mv-input' placeholder='Your name' autoComplete='name'
                                value={userName} onChange={e => setUserName(e.target.value)} maxLength={20} />)}
                        {field('mobileNumber', 'Mobile number',
                            <input {...inputProps('mobileNumber')} className='mv-input' placeholder='10-digit mobile number' type='tel'
                                inputMode='numeric' autoComplete='tel' value={mobileNumber} onChange={e => setMobileNumber(e.target.value)} maxLength={15} />,
                            'We will call this number if there is a delivery question.')}
                        {field('address', 'Delivery address',
                            <textarea {...inputProps('address')} className='mv-textarea' placeholder='House no., street, area, city'
                                autoComplete='street-address' value={address} onChange={e => setAddress(e.target.value)} rows='4'
                                autoFocus={!!editAddress} />)}
                        {field('state', 'State',
                            <input {...inputProps('state')} className='mv-input' placeholder='e.g. Tamil Nadu' autoComplete='address-level1'
                                value={state} onChange={e => setState(e.target.value)} maxLength={50} />)}
                        {field('pincode', 'Pincode',
                            <input {...inputProps('pincode')} className='mv-input' placeholder='6-digit pincode' inputMode='numeric'
                                autoComplete='postal-code' value={pincode} onChange={e => setPincode(e.target.value.replace(/\D/g, ''))} maxLength={6} />)}
                    </div>

                    {saveError && <div className='mv-alert mv-alert-error' role='alert'>{saveError}</div>}

                    <div className='mv-form-actions'>
                        <button type='submit' className='mv-btn mv-btn-primary' disabled={isSaving}>
                            {isSaving ? 'Saving…' : (editAddress ? 'Save and review order' : redirectPage ? 'Save and continue' : 'Save changes')}
                        </button>
                        {canCancel &&
                            <button type='button' className='mv-btn mv-btn-secondary' onClick={cancelEdit} disabled={isSaving}>Cancel</button>}
                    </div>
                </form>
            }

            <Toast message={toast} onDone={() => setToast('')} />
        </main>
    )

}
export default UpdateSettings
